import { isPlatformBrowser } from '@angular/common';
import { inject, Inject, Injectable, LOCALE_ID, NgZone, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { StoreSpecificConfigService } from '@scalefast/config-angular';
import { BehaviorSubject, from, map } from 'rxjs';
import { JebbitData, JebbitProduct } from './../../core/interfaces/jebbit.interface';
import { ContentfulClientService } from './contentful-client.service';

@Injectable({ providedIn: 'root' })
export class JebbitService {
  readonly JEBBIT_KEY = 'jebbit-selection';
  jebbitOrigin = 'https://meetyourbrush.oralb.com'; // FIXME : MOVE TO CONFIG FILE TO DIFF SPAIN AND FRANCE

  private jebbitData: BehaviorSubject<JebbitData> = new BehaviorSubject<JebbitData>({
    products: [],
    selected: [],
  });
  public jebbitData$ = this.jebbitData.asObservable();
  private jebbigId: string;
  private storeSpecificConfigService = inject(StoreSpecificConfigService);
  private contentfulClientService = inject(ContentfulClientService);

  constructor(
    @Inject(PLATFORM_ID) private platformId: any,
    @Inject(LOCALE_ID) public locale: string,
    private router: Router,
    private zone: NgZone,
  ) {
    this.jebbigId = this.storeSpecificConfigService.get().integrations.jebbitId;
  }

  captureJebbitMessage() {
    if (isPlatformBrowser(this.platformId)) {
      // Run event listener setup outside Angular zone for performance. Change detection was being triggered on every event.
      // Events may be triggered very often, mostly by third-party scripts, and it was bad for performance
      this.zone.runOutsideAngular(() => {
        window.addEventListener(
          'message',
          (event: MessageEvent) => {
            if (event.origin == this.jebbitOrigin && event.data.outcome && event.data.products) {
              sessionStorage.setItem(this.JEBBIT_KEY, JSON.stringify(event.data.products));
              this.zone.run(() => {
                this.router.navigateByUrl('/recommendedbrushes');
              });
            }
          },
          false,
        );
      });
    }
  }

  getJebbitDataFromContentful(withSelection: boolean = true) {
    if (this.jebbitData.value.products.length !== 0) {
      return;
    }

    if (isPlatformBrowser(this.platformId)) {
      const selections = JSON.parse(sessionStorage.getItem(this.JEBBIT_KEY) || '[]');
      const keys: string[] = [];
      selections.forEach((element: any) => {
        keys.push(element.id);
      });

      if (keys.length == 0 && withSelection) {
        let url = '/home';

        if (this.locale === 'es-ES') {
          url = '/mimejorcepillo';
        }
        if (this.locale === 'fr-FR') {
          url = '/MaMeilleureBrosse';
        }

        this.router.navigateByUrl(url);
        return;
      }

      from(this.getJebbitProducts())
        .pipe(map((data) => this.processResponse(data, keys)))
        .subscribe((res) => {
          this.jebbitData.next(res);
        });
    }
  }

  private async getJebbitProducts(): Promise<any> {
    const contentfulClient = await this.contentfulClientService.getClient();
    return contentfulClient.getEntries(
      Object.assign({
        content_type: 'page',
        'sys.id': this.jebbigId,
        locale: this.contentfulClientService.getLocale(),
        include: 7, // Minimum functional value
      }),
    );
  }

  private processResponse(res: any, keys: string[]): any {
    const products: JebbitProduct[] = [];
    const selected: JebbitProduct[] = [];

    for (const element of res.includes.Entry) {
      if (element.sys.contentType.sys.id !== 'productOverview' || !element.fields.jebbitId || !element.fields.shortTitle) {
        continue;
      }

      products.push(this.createTooltipHTML(element));

      if (element.fields.jebbitId) {
        keys.forEach((key, index) => {
          if (key == element.fields.jebbitId) {
            selected.push(element);
            keys.splice(index, 1);
          }
        });
      }
    }

    products.sort((product: JebbitProduct, product2: JebbitProduct) => {
      if (product.fields.jebbitId > product2.fields.jebbitId) {
        return -1;
      }
      if (product.fields.jebbitId < product2.fields.jebbitId) {
        return 1;
      }

      return 0;
    });

    return {
      products: products,
      selected: selected,
    };
  }

  private createTooltipHTML(product: JebbitProduct): JebbitProduct {
    /** The messages from contentful cannot be translated with $localize as that funcion works only in AOT,
     * meanwhile this translations are being loaded in JIT, this means we would be not capable of translating
     * the new variables after the localize translation life-cycle is done.
     */
    const tooltipOrder: any[] = [
      {
        name: 'smartDetection',
        enabled: true,
        translations: {
          'fr-FR': {
            heading: 'Capteur de pression',
            body: "Capteur de pression Smart Vous avertit lorsque le brossage est trop fort pour mieux protéger les gencives d'un brossage excessif.",
          },
          'es-ES': {
            heading: 'Sensor de presión',
            body: 'Te avisa cuando te cepillas demasiado fuerte para proteger mejor tus encías',
          },
        },
      },
      {
        name: 'IA',
        enabled: true,
        translations: {
          'fr-FR': {
            heading: 'Intelligence Artificielle',
            body: "Technologie de reconnaissance de brossage par intelligence artificielle intégrée L'intelligence Artificielle reconnaît votre style de brossage et vous guide pour couvrir toutes vos dents, afin de ne jamais oublier une zone.",
          },
          'es-ES': {
            heading: 'Inteligencia Artificial',
            body: 'La INTELIGENCIA ARTIFICIAL reconoce tu estilo de cepillado y te guía para limpiar todos los dientes y no dejar ninguna zona sin cepillar',
          },
        },
      },
      {
        name: 'bluetooth',
        enabled: true,
        translations: {
          'fr-FR': {
            heading: 'Bluetooth',
            body: "Connection Bluetooth Se connecte à l'application Oral-B pour améliorer vos habitudes de brossage.",
          },
          'es-ES': {
            heading: 'Bluetooth',
            body: 'Se conecta a la aplicación Oral-B para ayudar a mejorar tus hábitos de cepillado',
          },
        },
      },
      {
        name: 'battery',
        enabled: true,
        translations: {
          'fr-FR': {
            heading: 'Autonomie de la Batterie',
            body: 'Batterie longue durée - technologie de batterie longue durée supérieure - Lithium-Ion',
          },
          'es-ES': {
            heading: 'Batería',
            body: 'Batería recargable de larga duración',
          },
        },
      },
      {
        name: 'smartMode',
        enabled: false,
      },
      {
        name: 'timmer',
        enabled: true,
        translations: {
          'fr-FR': {
            heading: 'Minuteur de brossage',
            body: 'Le minuteur à quadrants intégré émet des pulsations toutes les 30 secondes pour un nettoyage de 2 minutes recommandé par les dentistes. ',
          },
          'es-ES': {
            heading: 'Temporizador',
            body: 'El temporizador con cambios de cuadrante integrado emite pulsaciones cada 30 segundos para el tiempo de limpieza de 2 mins que recomiendan los dentistas',
          },
        },
      },
      {
        name: 'display',
        enabled: true,
        translations: {
          'fr-FR': {
            heading: "Ecran d'affichage",
            body: "L'écran interactif offre un coaching en temps réel pour un nettoyage optimal.",
          },
          'es-ES': {
            heading: 'Pantalla',
            body: 'La pantalla interactiva ofrece consejos en tiempo real para una mejor limpieza',
          },
        },
      },
      {
        name: 'accesories',
        enabled: false,
      },
    ];

    product.fields.prePurchaseComparePoints.forEach((description, index) => {
      if (!tooltipOrder[index]['enabled']) {
        return;
      }

      const translations = tooltipOrder[index]?.['translations']?.[this.locale];

      if (translations) {
        description.fields._tooltipHTML = `<strong> ${translations.heading} </strong> <hr class="separatorComparisonTable"> <div> ${translations.body}</div>`;
      }
    });

    return product;
  }
}
